import React, { useEffect, useMemo, useRef, useState } from 'react';
import * as d3 from 'd3';
import * as THREE from 'three';
import dataCache from '../Utils/dataCache.js'; 
import JSZip from 'jszip';
import { Html } from '@react-three/drei';
import { useClickStore } from '../store/clickStore.js';
import { useThree } from '@react-three/fiber';

export default function BlockModels({ name, url , dx ,dy ,dz , size  , grade ,bmvisible}) {
  const instancedMeshRef = useRef();
  const setBlockModelLegend = useClickStore((state) => state.setBlockModelLegend);
  const blockModelOpacity = useClickStore((state) => state.blockModelOpacity);
  const blockModelGradeFilter = useClickStore((state) => state.blockModelGradeFilter);
  const setBlockModelGradeRange = useClickStore((state) => state.setBlockModelGradeRange);
  const setBlockModelGradeFilter = useClickStore((state) => state.setBlockModelGradeFilter);
  const selectionRegion = useClickStore((state) => state.selectionRegion);
  const { camera, size: viewportSize } = useThree();
  const [ogdata, setOGData] = useState([]);
  const [data, setData] = useState([]);
  const [count, setCount] = useState(0);
  const temp = new THREE.Object3D();
  const color = new THREE.Color();
  const [hoverInfo, setHoverInfo] = useState({ show: false, position: null, content: '' });
  const interval = useMemo(() => {
    if (blockModelGradeFilter?.min == null || blockModelGradeFilter?.max == null) {
      return [0, 20];
    }
    return [blockModelGradeFilter.min, blockModelGradeFilter.max];
  }, [blockModelGradeFilter]);
  
  const [instancesData, setInstancesData] = useState([]);
  const instancePositionsRef = useRef([]);
  const [selectedIds, setSelectedIds] = useState([]);

  const fetchDataWithProgress = async (url) => {
    const response = await fetch(url);
    const reader = response.body.getReader();
    const contentLength = +response.headers.get('Content-Length');

    let receivedLength = 0;
    const chunks = [];

    while (true) {
      const { done, value } = await reader.read();
      if (done) break;

      chunks.push(value);
      receivedLength += value.length;

      // Progress tracking for large file downloads
    }

    return new Blob(chunks);
  };

  // Load data when component mounts
  useEffect(() => {
    if(!bmvisible) return
      const fetchData = async () => {
        if (dataCache[url]) {
          setOGData(dataCache[url])
        }else{
          const userConfirmed = window.confirm(`Block model is not available on your cache , do you want to download it @ ${size}?`);
          if (userConfirmed) {
            // Downloading large dataset

            //////////////
            try {
              const blob = await fetchDataWithProgress(url);
              // Download complete, extracting data
              const zip = await JSZip.loadAsync(blob);
              const fileName = Object.keys(zip.files)[0]; // Assuming there's only one file in the zip
              const fileData = await zip.file(fileName).async('string');
              const parsedData = d3.csvParse(fileData);

              const filteredData = parsedData.filter(item => Number(item[grade]) > 0.1);

              console.log(parsedData,filteredData)
              setOGData(filteredData);
              dataCache[url] = filteredData; 
              
            } catch (error) {
              console.error('Error loading the zipped CSV file:', error);
            }
            
          }else{
            //set({ bmvisible: false  });
          }
        
        }
      }

    fetchData()
    
  }, [url,bmvisible]); // Only load data when URL changes

  useEffect(() => {
    setBlockModelLegend(name, grade, bmvisible);
    return () => setBlockModelLegend(name, grade, false);
  }, [bmvisible, grade, name, setBlockModelLegend]);

  useEffect(()=>{
    let tempArr = [];
    for (let i = ogdata.length - 1; i >= 0; i--) {
      // get the current object
      // check if the object's grade is within the range
      if (ogdata[i][grade] >= interval[0] && ogdata[i][grade]  <= interval[1]) {
        tempArr.push(ogdata[i])
      }
    }
    setData(tempArr)
    setCount(tempArr.length); 
  },[ogdata,interval])

  useEffect(() => {
    if (!ogdata.length) return;
    let min = Infinity;
    let max = -Infinity;
    for (let i = 0; i < ogdata.length; i++) {
      const value = Number(ogdata[i][grade]);
      if (!Number.isFinite(value)) continue;
      if (value < min) min = value;
      if (value > max) max = value;
    }
    if (!Number.isFinite(min) || !Number.isFinite(max)) return;
    setBlockModelGradeRange({ min, max });
    if (blockModelGradeFilter?.min == null || blockModelGradeFilter?.max == null) {
      setBlockModelGradeFilter({ min, max });
    }
  }, [ogdata, grade, setBlockModelGradeRange, setBlockModelGradeFilter, blockModelGradeFilter]);

  // Memoize colors calculation
  const colors = useMemo(() => {
    if (data.length === 0) return new Float32Array();
    const colorsArray = new Float32Array(data.length * 3);
    const selectedSet = new Set(selectedIds);
    for (let i = 0; i < data.length; i++) {
      const value = parseFloat(data[i][grade]);
      if (selectedSet.has(i)) {
        color.set(0xfff200);
      } else if (value >= 3) {
        color.set(0xac02b4);
      } else if (value >= 1.5) {
        color.set(0xff0000);
      } else if (value >= 0.6) {
        color.set(0x00ff00);
      } else {
        color.set(0x0000ff);
      }
      colorsArray[i * 3] = color.r;
      colorsArray[i * 3 + 1] = color.g;
      colorsArray[i * 3 + 2] = color.b;
    }
    return colorsArray;
  }, [data,bmvisible,selectedIds,grade]);

  useEffect(() => {
    let idata = [];
    const positions = [];
    if (data.length > 0 && instancedMeshRef.current) {
      for (let i = 0; i < data.length; i++) {
        const x = data[i].XC - 259333 + dx;
        const y = data[i].ZC - 1120 +dy;
        const z = 8156513 - data[i].YC +dz;
        temp.position.set(x, y, z);
        temp.updateMatrix();
        positions.push(new THREE.Vector3(x, y, z));
        instancedMeshRef.current.setMatrixAt(i, temp.matrix);
      }
      instancePositionsRef.current = positions;
      setInstancesData(data);
      // Update the instance
      instancedMeshRef.current.instanceMatrix.needsUpdate = true;
    }
  }, [data,bmvisible]);

  useEffect(() => {
    if (!selectionRegion || selectionRegion.phase !== 'complete') return;
    if (!bmvisible) return;
    if (!instancePositionsRef.current.length) return;
    if (!camera || !viewportSize?.width || !viewportSize?.height) return;

    const { type } = selectionRegion;
    const nextSelected = [];

    const projectToScreen = (vec) => {
      const projected = vec.clone().project(camera);
      return {
        x: (projected.x + 1) * 0.5 * viewportSize.width,
        y: (1 - (projected.y + 1) * 0.5) * viewportSize.height,
      };
    };

    const pointInBox = (pt, rect) => {
      const minX = Math.min(rect.x1, rect.x2);
      const maxX = Math.max(rect.x1, rect.x2);
      const minY = Math.min(rect.y1, rect.y2);
      const maxY = Math.max(rect.y1, rect.y2);
      return pt.x >= minX && pt.x <= maxX && pt.y >= minY && pt.y <= maxY;
    };

    const pointInPolygon = (pt, polygon) => {
      let inside = false;
      for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
        const xi = polygon[i].x;
        const yi = polygon[i].y;
        const xj = polygon[j].x;
        const yj = polygon[j].y;
        const intersect =
          yi > pt.y !== yj > pt.y &&
          pt.x < ((xj - xi) * (pt.y - yi)) / (yj - yi + 0.00001) + xi;
        if (intersect) inside = !inside;
      }
      return inside;
    };

    for (let i = 0; i < instancePositionsRef.current.length; i++) {
      const screenPt = projectToScreen(instancePositionsRef.current[i]);
      if (type === 'box' && selectionRegion.rect && pointInBox(screenPt, selectionRegion.rect)) {
        nextSelected.push(i);
      } else if (type === 'lasso' && selectionRegion.points?.length > 2 && pointInPolygon(screenPt, selectionRegion.points)) {
        nextSelected.push(i);
      }
    }

    setSelectedIds(nextSelected);
  }, [selectionRegion, camera, viewportSize]);

  // Memory cleanup on unmount
  useEffect(() => {
    return () => {
      if (instancedMeshRef.current) {
        // Dispose of geometry and material
        if (instancedMeshRef.current.geometry) {
          instancedMeshRef.current.geometry.dispose();
        }
        if (instancedMeshRef.current.material) {
          instancedMeshRef.current.material.dispose();
        }
      }
    };
  }, []);
  
  const handlePointerMove = (event) => {
    if (!bmvisible || event.instanceId == null) return;
    const item = data[event.instanceId];
    if (!item) return;
    const gradeValue = Number(item[grade]).toFixed(2);
    setHoverInfo({
      show: true,
      position: event.point.clone(),
      content: `Grade: ${gradeValue} | X:${Number(item.XC).toFixed(1)} Y:${Number(item.YC).toFixed(1)} Z:${Number(item.ZC).toFixed(1)}`,
    });
  };

  const handlePointerOut = () => {
    setHoverInfo({ show: false, position: null, content: '' });
  };

  return bmvisible ? (
    <group>
    <instancedMesh
      frustumCulled={false}
      ref={instancedMeshRef}
      args={[null, null, count]}
      name={name}
      userData={{ instancesData, gradeKey: grade }}
      onPointerMove={handlePointerMove}
      onPointerOut={handlePointerOut}
    >
        <boxGeometry args={[4.9, 4.9, 4.9]}>
        <instancedBufferAttribute attach="attributes-color" args={[colors, 3]} />
      </boxGeometry>
      <meshLambertMaterial
        vertexColors
        toneMapped={false}
        transparent
        opacity={blockModelOpacity}
      />
    </instancedMesh>
    {hoverInfo.show && hoverInfo.position && (
      <Html position={hoverInfo.position} center sprite distanceFactor={200} transform>
        <div className="bg-black/70 text-white text-[10px] px-1.5 py-0.5 rounded">
          {hoverInfo.content}
        </div>
      </Html>
    )}
      
  </group>

  ) : null; 

}
